import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Subscription } from 'rxjs';
import type { FileDownloadEvent } from './file-download.service';
import { DownloadTask, FileDownloadError } from './file-download.service';

@UntilDestroy({ className: 'FileDownloadAlertContentComponent' })
@Component({
  standalone: false,
  template: `
    <div class="DownloadAlertBody">
      <ng-container i18n="File downloads notification status">
        Downloading {{ download.name | truncateFilename: 25 }}
      </ng-container>
      <div class="DownloadAlertDetails">
        <div class="DownloadAlertDetails--status mr-xxsmall">
          @if (changes?.status === 'success') {
            <ng-container i18n="File download notification status">
              Download complete
            </ng-container>
          }
          @if (changes?.status === 'error') {
            <ng-container [ngSwitch]="errorCode">
              <ng-container
                *ngSwitchCase="FileDownloadError.PERMISSION_NOT_GRANTED"
                i18n="Permission denied error message for download files"
              >
                Permission denied. Please enable permissions in app settings.
              </ng-container>
              <ng-container
                *ngSwitchCase="FileDownloadError.UNKNOWN"
                i18n="General error message for download files"
              >
                Download failed
              </ng-container>
              <ng-container
                *ngSwitchDefault
                i18n="General error message for download files"
              >
                Download failed
              </ng-container>
            </ng-container>
          }
          @if (changes?.status === 'running' && changes.progressPercentage) {
            <fl-progress-bar
              label="Download progress for {{ download?.name }}"
              i18n-label="File download notification progress bar"
              [progressPercentage]="changes.progressPercentage"
            ></fl-progress-bar>
          }
        </div>
        @if (changes?.status === 'running') {
          <fl-link
            i18n="File download notification button"
            (click)="handleCancel()"
          >
            Cancel
          </fl-link>
        }
        @if (changes?.status === 'success') {
          <fl-link
            i18n="File download notification button"
            (click)="handleOpen()"
          >
            Open file
          </fl-link>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./file-download-alert-content.component.scss'],
})
export class FileDownloadAlertContentComponent implements OnInit, OnDestroy {
  FileDownloadError = FileDownloadError;

  @Input() download: DownloadTask;
  @Input() onCancel: () => void;
  @Input() onOpen: () => void;

  changes: FileDownloadEvent;
  private changesSubscription?: Subscription;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.changesSubscription = this.download.changes$
      .pipe(untilDestroyed(this))
      .subscribe(changes => {
        this.changes = changes;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    if (this.changesSubscription) {
      this.changesSubscription.unsubscribe();
    }
  }

  handleCancel(): void {
    this.onCancel();
  }

  handleOpen(): void {
    this.onOpen();
  }

  get errorCode(): FileDownloadError | null {
    if (this.changes && this.changes.status === 'error') {
      return this.changes.errorCode;
    }
    return null;
  }
}
